import type { MenuBoardContent } from '../types.js';
import type { MenuTemplate } from './common.js';
import {
  FC_THEME,
  FC_VARIANTS,
  fcBlocks,
  fcColumn,
  fcCss,
  fcEmpty,
  fcPaginate,
  fcPhoto,
  fcPhotoCount,
  fcPhotoIds,
  fcTitle,
  fcTitleLayout,
  type FcTitleSpec,
} from './food-collage-mosaic.js';

/**
 * Food collage, photo column: three dish photos stacked full height down one side of the
 * chalkboard; the title and two labelled lists fill the rest.
 */

const S = '.mb-t-food-collage-columns';
const P = 'fcc';

const TITLE_LANDSCAPE: FcTitleSpec = { width: 74, script: 3.2, caps: 5.6, maxLines: 1 };
const TITLE_PORTRAIT: FcTitleSpec = { width: 37, script: 3, caps: 4.8, maxLines: 3 };

function geometry(board: MenuBoardContent, portrait: boolean) {
  const photos = fcPhotoCount(board, 3);
  const title = fcTitleLayout(board, portrait ? TITLE_PORTRAIT : TITLE_LANDSCAPE);
  if (portrait) return { photos, title, columns: [fcColumn(board, 116 - title.height - 2, 37)] };
  const capacity = 63.5 - title.height - 2;
  return { photos, title, columns: [fcColumn(board, capacity, 38), fcColumn(board, capacity, 38)] };
}

export const foodCollageColumns: MenuTemplate = {
  id: 'food-collage-columns',
  name: 'Food collage · Photo column',
  description:
    'The chalkboard collage look with a tall column of three dish photos, the big title at the top and two labelled lists beside it.',
  defaultTheme: FC_THEME,
  variants: FC_VARIANTS,
  cuisines: ['burgers', 'pizza', 'pasta', 'cafe', 'drinks'],
  family: { id: 'food-collage', name: 'Food Collage', layout: 'Photo column' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return fcPaginate(board, geometry(board, orientation === 'portrait').columns);
  },

  render(ctx) {
    const portrait = ctx.orientation === 'portrait';
    const { photos, title } = geometry(ctx.board, portrait);
    const tiles = fcPhotoIds(ctx, photos).map((id) => fcPhoto(ctx, id, P)).join('');
    const cols = ctx.page.columns
      .map((c, i) => `<div class="fcc-list">${fcBlocks(ctx, c, P, portrait ? 37 : 38)}${i === 0 ? fcEmpty(ctx) : ''}</div>`)
      .join('');
    return [
      `<div class="fcc-wrap${tiles ? '' : ' fcc-solo'}">`,
      tiles ? `<div class="fcc-photos">${tiles}</div>` : '',
      `<div class="fcc-main">${fcTitle(ctx, P, title)}<div class="fcc-cols">${cols}</div></div>`,
      '</div>',
    ].join('');
  },

  css:
    fcCss(S, P) +
    `
${S} .fcc-wrap{flex:1;min-height:0;display:grid;grid-template-columns:34em minmax(0,1fr);grid-template-rows:minmax(0,1fr);gap:2.6em}
${S}.mb-portrait .fcc-wrap{grid-template-columns:22em minmax(0,1fr);gap:2.4em}
${S} .fcc-wrap.fcc-solo{grid-template-columns:minmax(0,1fr)}
${S} .fcc-photos{display:flex;flex-direction:column;gap:1.4em;min-height:0;height:100%}
${S} .fcc-photos .fcc-ph{flex:1 1 0}
${S} .fcc-main{min-width:0;min-height:0;display:flex;flex-direction:column}
${S} .fcc-title{--script:3.2em;padding-bottom:.4em}
${S}.mb-portrait .fcc-title{--script:3em}
${S} .fcc-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:0 2.6em;margin-top:1.6em}
${S}.mb-portrait .fcc-cols{grid-template-columns:minmax(0,1fr)}
`,
};
